import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useImperativeHandle,
  useCallback,
} from "react";
import { useDebouncedState } from "@mantine/hooks";

import _ from "lodash";
import {
  Button,
  Select,
  MultiSelect,
  Box,
  Group,
  Paper,
  Text,
} from "@mantine/core";
import { useServerApi } from "../hooks/userServerApi";
import { useForceUpdate } from "@mantine/hooks";
import ReactJson from "react-json-view";
import useDeepCompareEffect from "../hooks/useDeepCompareEffect";
import MultiRemoteSelect from "./multiRemoteSelect";
import FormReadOnlyField from "./formReadOnlyField";
import { formatFormFieldDisplay } from "../helpers/formatFormFieldDisplay";
import { DATA_AGE_TYPES } from "../data/options";
import { useTranslation } from "react-i18next";
import { dropdownZIndex } from "../theme";
// window.addEventListener("error", function (e) {
//   console.error(e.message);
//   // prevent React's listener from firing
//   e.stopImmediatePropagation("ResizeObserver loop limit exceeded");
//   // prevent the browser's console error message
//   e.preventDefault();
// });

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

const resizeObserver = new ResizeObserver(
  debounce((entries) => {
    for (let entry of entries) {
      // Handle resize
    }
  }, 100) // Adjust the debounce time as needed
);

const RemoteSelect2 = (
  {
    form,
    name,
    label,
    value,
    sort,
    apiEntity,
    valueField = "_id",
    labelField,
    labelRender,
    placeholder = "Pick One",
    clearable = true,
    searchFields = ["code"],
    preQuery,
    // required,
    multiple = false,
    onDataChange,
    onKeydown,
    clearAfterChange = false,
    disabled = false,
    readOnly = false,
    variant = "default",
    pageSize = 20,
    zIndex = dropdownZIndex,
    width = "100%",
    size = "sm",
    select,
    onChange: onChangeProp,
    keepUnknownValue = false,
    allowClear,
    labelHint,
    formMode: _formMode,
    formAction: _formAction,
    ...props
  },
  ref
) => {
  useImperativeHandle(
    ref,
    () => {
      return {
        clear: clearSelect,
      };
    },
    []
  );
  const [fieldValue, setFieldValue] = useState(); //selected value
  const [options, setOptions] = useState([]); //Converted to Options
  const [data, setData] = useState([]); //Orginal data sets
  const [selectedRecord, setSelectedRecord] = useState(null);
  const selectedRecordRef = useRef(null);
  const [searchText, setSearchText] = useDebouncedState("", 200);

  const [searchQuery, setSearchQuery] = useState({});
  const [api] = useServerApi();

  const currentPage = 1; //Always display first page
  const { t: translate } = useTranslation();
  const locked = disabled || readOnly || Boolean(form?.isReadOnly?.());
  const getDisplayValue = () => {
    let v = value || _.get(form?.values, name);
    if (!v) return null;

    if (!multiple) {
      // console.log("getDisplayValue1", name, v);
      v = typeof v === "object" ? v?._id : v;
    }
    if (multiple && Array.isArray(v)) {
      // console.log("getDisplayValue2", name, v);

      v = v.map((value) => value?._id || value);
    }
    return v;
  };

  const displayValue = getDisplayValue();

  useDeepCompareEffect(() => {
    setFieldValue(displayValue);
  }, [displayValue]);

  const rememberSelected = (row) => {
    selectedRecordRef.current = row;
    setSelectedRecord((current) => {
      if (!row && !current) return current;
      if (!row || !current) return row;
      const currentId = _.get(current, valueField) ?? current._id;
      const nextId = _.get(row, valueField) ?? row._id;
      if (
        currentId != null &&
        nextId != null &&
        String(currentId) === String(nextId) &&
        current.ageType === row.ageType
      ) {
        return current;
      }
      return row;
    });
  };

  useEffect(() => {
    if (typeof labelHint !== "function" || multiple) return undefined;
    if (fieldValue == null || fieldValue === "") {
      rememberSelected(null);
      return undefined;
    }

    const row = (Array.isArray(data) ? data : []).find(
      (item) => String(_.get(item, valueField)) === String(fieldValue)
    );
    if (row) {
      rememberSelected(row);
      return undefined;
    }

    const current = selectedRecordRef.current;
    const currentId = current
      ? _.get(current, valueField) ?? current._id
      : null;
    if (currentId != null && String(currentId) === String(fieldValue)) {
      return undefined;
    }

    const raw = value && typeof value === "object" ? value : null;
    const rawId = raw ? _.get(raw, valueField) ?? raw._id : null;
    if (
      raw?.ageType &&
      rawId != null &&
      String(rawId) === String(fieldValue)
    ) {
      rememberSelected(raw);
      return undefined;
    }

    let cancelled = false;
    api
      .search({
        apiEntity,
        pageSize: 1,
        currentPage: 1,
        searchQuery: { [valueField]: fieldValue },
        select,
        byAggregation: false,
      })
      .then((result) => {
        if (cancelled) return;
        const found = result?.docs?.[0];
        const foundId = found
          ? _.get(found, valueField) ?? found._id
          : null;
        if (found && String(foundId) === String(fieldValue)) {
          rememberSelected(found);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [labelHint, multiple, fieldValue, data, value, valueField, apiEntity, select]);

  const handleChange = useCallback((v) => {
    // console.log("handleChange", v);
    // Use requestAnimationFrame to defer state updates
    // This prevents ResizeObserver loop errors when clearing/selecting
    requestAnimationFrame(() => {
      setFieldValue(v);
      setFormValue(v);
      notifyDataChange(v);
      onChangeProp?.(v);
    });
  });

  const clearSelect = () => {
    setFieldValue(null);
  };

  const setFormValue = (v) => {
    if (!form || !name) return;
    // if (!value || _.isEmpty(value)) return;
    form.markUserEdited?.();
    form.setFieldValue(name, v);
  };

  const notifyDataChange = (v) => {
    if (!_.isFunction(onDataChange)) return;
    
    // Find the data for the selected value
    const selectedData = data.find((d) => _.get(d, valueField) === v);
    
    // Call onDataChange with the found data (or null if not found/cleared)
    // Use requestAnimationFrame to avoid ResizeObserver loop during layout changes
    requestAnimationFrame(() => {
      onDataChange(selectedData || null, form);
    });
    
    if (clearAfterChange) {
      setFieldValue(null);
    }
  };

  const getLabel = (data) => {
    // console.log("getLabel", name, data);
    if (labelRender) return labelRender(data);
    if (labelField) return _.get(data, labelField);
    return "";
  };

  const unknownValueOption = (current) => {
    if (!keepUnknownValue) return null;
    if (typeof current !== "string" && typeof current !== "number") return null;
    if (current === "") return null;
    return { value: current, label: String(current) };
  };

  const fetchFieldValueData = async () => {
    if (!fieldValue) return;
    // console.log("fetchFieldValueData before", name, options, fieldValue);
    if (options.findIndex((o) => o.value === fieldValue) !== -1) return;

    let result = await api.search({
      apiEntity,
      pageSize,
      currentPage: 1,
      sort,
      searchQuery: { [valueField]: fieldValue },
      select,
      byAggregation: false,
    });
    // console.log("fetchFieldValueData", name, result);
    const rows = result?.docs;
    if (!rows || _.isEmpty(rows)) {
      const fallback = unknownValueOption(fieldValue);
      if (fallback) {
        setOptions((prev) =>
          prev.some((option) => option.value === fieldValue)
            ? prev
            : [fallback, ...prev]
        );
      }
      return;
    }

    let o = rows.map((r) => ({
      value: _.get(r, valueField),
      label: getLabel(r),
    }));
    setOptions([...o, ...options]);
  };

  const fetchSeq = useRef(0);

  const fetchData = async () => {
    const seq = ++fetchSeq.current;
    try {
      if (_.isEmpty(searchQuery) && !_.isEmpty(preQuery)) return;

      // console.log("fetchData", searchQuery);
      let result = await api.search({
        apiEntity,
        pageSize,
        currentPage,
        sort,
        searchQuery,
        byAggregation: false,
        select,
      });

      if (seq !== fetchSeq.current) return;
      handleFetchResult(result?.docs ?? []);
      fetchFieldValueData();
    } catch (e) {
      if (seq !== fetchSeq.current) return;
      // Preserve the currently selected option even on error
      setOptions((prevOptions) => {
        if (!fieldValue) return [];
        const currentOption = prevOptions.find((o) => o.value === fieldValue);
        if (currentOption) return [currentOption];
        const fallback = unknownValueOption(fieldValue);
        return fallback ? [fallback] : [];
      });
      setData([]);
    }
  };

  const handleFetchResult = (rows) => {
    const safe = Array.isArray(rows) ? rows : [];
    setData(safe);
    const newOptions = safe.map((r) => ({
      value: _.get(r, valueField),
      label: getLabel(r),
    }));
    
    // Preserve the currently selected option if it exists
    // This prevents the clear button from flickering during search
    setOptions((prevOptions) => {
      if (!fieldValue) return newOptions;
      
      // Check if current selection is in new options
      const currentInNew = newOptions.find((o) => o.value === fieldValue);
      if (currentInNew) return newOptions;
      
      // Keep the current selection from previous options
      const currentOption = prevOptions.find((o) => o.value === fieldValue);
      if (currentOption) {
        return [currentOption, ...newOptions];
      }

      const fallback = unknownValueOption(fieldValue);
      if (fallback) return [fallback, ...newOptions];

      return newOptions;
    });
  };

  const handleRegexSpecialChar = (text) => {
    // console.log("handleRegexSpecialChar", text);
    const specialTxt = [
      "\\",
      "^",
      "$",
      ".",
      "|",
      "?",
      "*",
      "+",
      "(",
      ")",
      "[",
      "]",
      "{",
      "}",
    ];

    let result = text;
    specialTxt.forEach((special) => {
      result = result.replace(special, `\\${special}`);
    });
    return result;
  };

  const buildSearchQuery = (searchText, searchFields) => {
    //Remove sepecical character
    // const txt = const searchText.replace(")", "\\)").replace("(", "\\(");

    if (!searchText) {
      return preQuery ? preQuery : {};
    }

    let txt = handleRegexSpecialChar(searchText)?.trim();
    let searchArr = searchFields?.map((field) => ({
      [field]: { $regex: txt, $options: "i" },
    }));
    if (!preQuery) return { $or: searchArr };
    return { $and: [preQuery, { $or: searchArr }] };

    //split the search text by space
    // let txtArr = searchText.split(" ");
    // txtArr = txtArr.map((txt) => handleRegexSpecialChar(txt)?.trim());

    // let searchArr = [];
    // txtArr.forEach((txt) => {
    //   searchArr = [
    //     ...searchArr,
    //     ...searchFields?.map((field) => ({
    //       [field]: { $regex: txt, $options: "i" },
    //     })),
    //   ];
    // });

    // if (!preQuery) return { $or: searchArr };
    // return { $and: [preQuery, { $or: searchArr }] };
  };

  const handleSearchTextChange = (text) => {
    setSearchText(text);
  };

  useDeepCompareEffect(() => {
    const query = buildSearchQuery(searchText, searchFields);
    setSearchQuery((current) => (_.isEqual(current, query) ? current : query));
  }, [searchText, preQuery, searchFields]);

  // const handleSearchChange = useCallback(
  //   (searchText) => {
  //     let query = buildSearchQuery(searchText, searchFields);
  //     setSearchQuery(query);
  //   },
  //   [searchFields, searchText]
  // );

  useEffect(() => {
    if (locked) return;
    fetchData();
  }, [searchQuery]);

  useEffect(() => {
    fetchFieldValueData();
  }, [fieldValue]);

  const hint =
    typeof labelHint === "function" && selectedRecord
      ? labelHint(selectedRecord)
      : null;
  const visualLabel = hint ? (
    <>
      {label}
      <Text
        component="span"
        size="xs"
        color="dimmed"
        weight={500}
        sx={{ order: 1, marginLeft: 8, lineHeight: 1.2 }}
      >
        ({hint})
      </Text>
    </>
  ) : (
    label
  );
  const hintStyles = hint
    ? {
        label: {
          display: "flex",
          alignItems: "baseline",
          flexWrap: "wrap",
        },
      }
    : null;
  const styles = hintStyles
    ? _.merge(
        {},
        typeof props.styles === "function" ? {} : props.styles,
        hintStyles
      )
    : props.styles;

  if (locked) {
    const raw = value ?? _.get(form?.values, name);
    const fromOptions = multiple
      ? (Array.isArray(fieldValue) ? fieldValue : [])
          .map(
            (item) =>
              options.find((option) => option.value === item)?.label
          )
          .filter(Boolean)
          .join(", ")
      : options.find((option) => option.value === fieldValue)?.label;
    const formatted = formatFormFieldDisplay(raw, {
      kind: "remoteSelect",
      props: { labelField, labelRender },
    });
    return (
      <FormReadOnlyField
        label={
          hint && typeof label === "string" ? `${label} (${hint})` : label
        }
        value={fromOptions || formatted}
      />
    );
  }

  return (
    <>
      {/* {searchText} */}
      {/* fieldValue:{JSON.stringify(fieldValue)}
      displayValue: {JSON.stringify(displayValue)} <br></br> */}
      {/* multiple: {multiple ? "true" : "false"} */}
      {/* sort: {JSON.stringify(sort)} <br></br>  */}
      {/* {pageSize} */}
      {multiple ? (
        <MultiRemoteSelect
          form={form}
          name={name}
          label={label}
          value={fieldValue ?? value}
          onChange={onChangeProp}
          sort={sort}
          pageSize={pageSize}
          apiEntity={apiEntity}
          valueField={valueField}
          labelField={labelField}
          labelRender={labelRender}
          searchFields={searchFields}
          required={props.required}
          preQuery={preQuery}
          size={size}
          placeholder={placeholder}
          disabled={disabled}
          {...props}
        />
      ) : (
        /* <MultiSelect
						// {...props}
						value={fieldValue}
						label={label}
						placeholder={placeholder}
						clearable
						searchable
						onChange={handleChange}
						onSearchChange={handleSearchChange}
						onKeyDown={onKeydown}
						data={options}
						disabled={disabled}
						variant={variant}
					/> */

        <Box sx={{ width }}>
          <Select
            {..._.omit(props, [
              "allowClear",
              "searchFields",
              "formMode",
              "formAction",
              "apiEntity",
              "valueField",
              "labelField",
              "labelRender",
              "preQuery",
              "multiple",
              "onDataChange",
              "clearAfterChange",
              "pageSize",
              "select",
              "keepUnknownValue",
            ])}
            value={fieldValue ?? null}
            label={visualLabel}
            placeholder={translate(placeholder)}
            styles={styles}
            clearable={clearable && allowClear !== false}
            required={props.required}
            searchable
            onChange={handleChange}
            onSearchChange={handleSearchTextChange}
            onKeyDown={onKeydown}
            data={options}
            disabled={disabled}
            variant={variant}
            size={size}
            withinPortal
            zIndex={zIndex}
          />
        </Box>
      )}
    </>
  );
};

export default React.forwardRef(RemoteSelect2);
